import React, { useState, useRef, useEffect, useMemo } from 'react';
import {
  TextField,
  IconButton,
  Paper,
  List,
  ListItem,
  ListItemText,
  Popper,
  Fade,
  Chip,
  Typography,
  CircularProgress,
  InputAdornment,
  Tooltip,
} from '@mui/material';
import {
  Search as SearchIcon,
  Clear as ClearIcon,
  History as HistoryIcon,
  Help as HelpIcon,
  AutoAwesome as AutoAwesomeIcon,
} from '@mui/icons-material';

interface GraphQueryBarProps {
  onQuery: (query: string) => Promise<void>;
  queryHistory: string[];
  disabled?: boolean;
}

/**
 * GraphQueryBar Component
 * 
 * Provides natural language query interface for the knowledge graph.
 * Features autocomplete, query history, and example queries.
 * 
 * Natural Language Examples:
 * - "Show me all messages from last week"
 * - "Find documents about project planning"
 * - "What topics are connected to John Doe?"
 * - "Show me my recent conversations"
 * - "Find all todos assigned to me" (future)
 */
const GraphQueryBar: React.FC<GraphQueryBarProps> = ({
  onQuery,
  queryHistory,
  disabled = false,
}) => {
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [suggestions, setSuggestions] = useState<string[]>([]);
  
  const inputRef = useRef<HTMLInputElement>(null);
  const anchorRef = useRef<HTMLDivElement>(null);

  // Example queries for user guidance
  const exampleQueries = useMemo(() => [
    'Show me all messages from last week',
    'Find documents about product roadmap',
    'What concepts are trending this month?',
    'Show connections between team members',
    'Find all messages mentioning AI',
    'Show my most active conversations',
    'What topics did we discuss yesterday?',
    'Find all documents shared by Maya',
    'Show organizational knowledge nodes',
    'What are the main discussion topics?',
    // Future todo queries (commented for documentation)
    // 'Show my open todos',
    // 'Find todos due this week',
    // 'What tasks are blocking others?',
    // 'Show todos assigned to my team',
  ], []);

  // Generate suggestions based on input
  useEffect(() => {
    if (query.length > 2) {
      const filteredSuggestions = exampleQueries
        .filter(q => q.toLowerCase().includes(query.toLowerCase()))
        .slice(0, 5);
      
      // Add history matches
      const historyMatches = queryHistory
        .filter(q => q.toLowerCase().includes(query.toLowerCase()))
        .slice(0, 3);
      
      setSuggestions(Array.from(new Set([...historyMatches, ...filteredSuggestions])));
      setShowSuggestions(true);
    } else {
      setShowSuggestions(false);
    }
  }, [query, queryHistory, exampleQueries]);

  // Handle query submission
  const handleSubmit = async (e?: React.FormEvent) => {
    e?.preventDefault();
    
    if (!query.trim() || loading || disabled) return;
    
    setLoading(true);
    setShowSuggestions(false);
    setShowHistory(false);
    
    try {
      await onQuery(query);
      setQuery('');
    } catch (error) {
      console.error('Query failed:', error);
    } finally {
      setLoading(false);
    }
  };

  // Handle suggestion selection
  const handleSuggestionClick = (suggestion: string) => {
    setQuery(suggestion);
    setShowSuggestions(false);
    setShowHistory(false);
    inputRef.current?.focus();
  };

  // Handle history toggle
  const handleHistoryToggle = () => {
    setShowHistory(!showHistory);
    setShowSuggestions(false);
  };

  // Handle clear
  const handleClear = () => {
    setQuery('');
    setShowSuggestions(false);
    setShowHistory(false);
    inputRef.current?.focus();
  };

  // Quick query chips
  const quickQueries = [
    { label: 'Recent Messages', query: 'Show me messages from today' },
    { label: 'My Documents', query: 'Find my documents' },
    { label: 'Team Activity', query: 'Show team collaboration' },
    { label: 'Important Topics', query: 'What are the key topics?' },
  ];

  return (
    <div>
      <form onSubmit={handleSubmit}>
        <div ref={anchorRef} style={{ position: 'relative' }}>
          <TextField
            ref={inputRef}
            fullWidth
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Ask anything about your knowledge graph... (e.g., 'Show me all messages about the new project')"
            disabled={disabled || loading}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <AutoAwesomeIcon color="primary" />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  {loading ? (
                    <CircularProgress size={20} />
                  ) : (
                    <>
                      {query && (
                        <IconButton size="small" onClick={handleClear}>
                          <ClearIcon fontSize="small" />
                        </IconButton>
                      )}
                      <Tooltip title="View query history">
                        <IconButton 
                          size="small" 
                          onClick={handleHistoryToggle}
                          color={showHistory ? 'primary' : 'default'}
                        >
                          <HistoryIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Help">
                        <IconButton size="small">
                          <HelpIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <IconButton 
                        color="primary" 
                        onClick={() => handleSubmit()}
                        disabled={!query.trim() || loading || disabled}
                      >
                        <SearchIcon />
                      </IconButton>
                    </>
                  )}
                </InputAdornment>
              ),
            }}
          />
        </div>
      </form>

      {/* Quick Query Chips */}
      <div style={{ marginTop: 8, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        {quickQueries.map((quick) => (
          <Chip
            key={quick.label}
            label={quick.label}
            size="small"
            onClick={() => handleSuggestionClick(quick.query)}
            disabled={loading || disabled}
            color="primary"
            variant="outlined"
            style={{ cursor: 'pointer' }}
          />
        ))}
      </div>

      {/* Suggestions Dropdown */}
      <Popper
        open={showSuggestions && suggestions.length > 0}
        anchorEl={anchorRef.current}
        placement="bottom-start"
        transition
        style={{ width: anchorRef.current?.offsetWidth, zIndex: 1300 }}
      >
        {({ TransitionProps }) => (
          <Fade {...TransitionProps} timeout={350}>
            <Paper elevation={3}>
              <List dense>
                {suggestions.map((suggestion, index) => (
                  <ListItem
                    key={index}
                    button
                    onClick={() => handleSuggestionClick(suggestion)}
                  >
                    <ListItemText 
                      primary={suggestion}
                      primaryTypographyProps={{
                        style: {
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        },
                      }}
                    />
                  </ListItem>
                ))}
              </List>
            </Paper>
          </Fade>
        )}
      </Popper>

      {/* History Dropdown */}
      <Popper
        open={showHistory && queryHistory.length > 0}
        anchorEl={anchorRef.current}
        placement="bottom-start"
        transition
        style={{ width: anchorRef.current?.offsetWidth, zIndex: 1300 }}
      >
        {({ TransitionProps }) => (
          <Fade {...TransitionProps} timeout={350}>
            <Paper elevation={3}>
              <div style={{ padding: 8, borderBottom: '1px solid rgba(0,0,0,0.12)' }}>
                <Typography variant="caption" color="text.secondary">
                  Recent Queries
                </Typography>
              </div>
              <List dense style={{ maxHeight: 300, overflow: 'auto' }}>
                {queryHistory.map((historyItem, index) => (
                  <ListItem
                    key={index}
                    button
                    onClick={() => handleSuggestionClick(historyItem)}
                  >
                    <ListItemText 
                      primary={historyItem}
                      secondary={`Query ${queryHistory.length - index}`}
                      primaryTypographyProps={{
                        style: {
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        },
                      }}
                    />
                  </ListItem>
                ))}
              </List>
            </Paper>
          </Fade>
        )}
      </Popper>
    </div>
  );
};

export default GraphQueryBar;